import { useEffect, useId, useRef, useState } from "react";

/* One behaviour for every modal (ACCESSIBILITY_PLAN.md session 8).

   On open: remember what had focus, move focus into the panel (its first
   focusable control, or the panel itself), keep Tab and Shift+Tab inside it,
   close on Escape, and stop the page behind from scrolling. On close: put
   focus back where it was, so a keyboard user carries on from the button that
   opened it instead of starting again at the top of the page.

   Usage:
     const dialog = useDialog({ open, onClose });
     {open && (
       <div className="fixed inset-0 ...">
         <div {...dialog.props} className="...panel">
           <h2 id={dialog.titleId}>Title</h2>

   `open` defaults to true for components that only mount while open. Leave
   `onClose` out for a gate that must be answered (terms, agreements): Escape
   then does nothing, and Tab is still kept inside. */

const FOCUSABLE = [
  "a[href]", "area[href]", "button:not([disabled])", "input:not([disabled]):not([type=hidden])",
  "select:not([disabled])", "textarea:not([disabled])", "iframe", "summary",
  "[contenteditable=true]", "[tabindex]:not([tabindex='-1'])",
].join(",");

// Open dialogs, innermost last. Only the top one answers keys, so Escape in a
// confirm opened over a form closes the confirm and not both.
const stack = [];

function focusables(panel) {
  return Array.from(panel.querySelectorAll(FOCUSABLE)).filter(
    (el) => !el.closest("[inert]") && (el.offsetParent !== null || el.getClientRects().length > 0),
  );
}

export default function useDialog({ open = true, onClose } = {}) {
  // A callback ref, so the effect follows the panel element itself: a modal
  // that swaps its panel for another (the contract chooser hands over to the
  // form it opens) releases focus and the scroll lock when the panel goes.
  const [panel, ref] = useState(null);
  const titleId = useId();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Who had focus when it opened, read during render: a child that focuses
  // itself on mount (the hero's text box) runs its effect before ours, so by
  // then document.activeElement is already inside the panel.
  const openerRef = useRef(null);
  if (!open) openerRef.current = null;
  else if (openerRef.current === null && typeof document !== "undefined") {
    openerRef.current = document.activeElement;
  }

  useEffect(() => {
    if (!open || !panel) return undefined;

    // If the element we saw has gone (a modal that replaced the one that held
    // the button), whatever has focus now is the way back.
    const seen = openerRef.current;
    const opener = seen && document.contains(seen) && !panel.contains(seen) ? seen : document.activeElement;
    stack.push(panel);

    if (!panel.contains(document.activeElement)) {
      const first = focusables(panel)[0];
      if (first) first.focus({ preventScroll: true });
      else panel.focus({ preventScroll: true });
    }

    const onKey = (e) => {
      if (stack[stack.length - 1] !== panel) return;
      if (e.key === "Escape") {
        if (onCloseRef.current) {
          e.stopPropagation();
          onCloseRef.current();
        }
        return;
      }
      if (e.key !== "Tab") return;
      const list = focusables(panel);
      if (!list.length) {
        e.preventDefault();
        panel.focus({ preventScroll: true });
        return;
      }
      const first = list[0];
      const last = list[list.length - 1];
      const at = document.activeElement;
      if (e.shiftKey && (at === first || !panel.contains(at))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (at === last || !panel.contains(at))) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);

    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      const i = stack.lastIndexOf(panel);
      if (i !== -1) stack.splice(i, 1);
      if (!stack.length) document.body.style.overflow = overflow;
      if (opener && typeof opener.focus === "function" && document.contains(opener)) {
        opener.focus({ preventScroll: true });
      }
    };
  }, [open, panel]);

  return {
    ref,
    titleId,
    props: {
      ref,
      role: "dialog",
      "aria-modal": "true",
      "aria-labelledby": titleId,
      tabIndex: -1,
    },
  };
}
